import type { AchievementDto, GameDto, GameProgressDto } from "@steam/contracts";
import { formatPlaytimeExact } from "@steam/domain";

import type { Translate } from "../i18n/i18n";
import { formatDay, joined, neverLaunched } from "./library";

export type AchievementFilter = "all" | "unlocked" | "locked";

export type AchievementRow = {
  readonly apiName: string;
  readonly name: string;
  readonly description: string;
  readonly iconUrl: string;
  readonly unlocked: boolean;
  readonly dateLabel: string;
};

export type TimelineItem = {
  readonly apiName: string;
  readonly name: string;
  readonly iconUrl: string;
  readonly timeLabel: string;
};

export type TimelineDay = {
  readonly key: string;
  readonly day: string;
  readonly year: string;
  readonly countLabel: string;
  readonly items: readonly TimelineItem[];
};

export type GameSummary = {
  readonly percentage: number | null;
  readonly rateLabel: string;
  readonly fraction: string;
  readonly remaining: string;
  readonly lastUnlock: string;
  readonly meta: string;
};

export type FilterCounts = {
  readonly all: number;
  readonly unlocked: number;
  readonly locked: number;
};

const pad = (value: number): string => String(value).padStart(2, "0");

/** "22:09", in the device's own time zone. */
const formatTime = (iso: string): string => {
  const date = new Date(iso);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const earnedAt = (achievement: AchievementDto): number =>
  achievement.unlockedAt ? Date.parse(achievement.unlockedAt) : -Infinity;

export const buildFilterCounts = (progress: GameProgressDto): FilterCounts => {
  const all = progress.achievements.length;
  const unlocked = progress.achievements.filter((a) => a.unlocked).length;
  return { all, unlocked, locked: all - unlocked };
};

/** The chip for one filter: what it shows, and how many that is. */
export const filterLabel = (
  filter: AchievementFilter,
  counts: FilterCounts,
  t: Translate,
): string => t(`game.filters.${filter}`, { count: counts[filter] });

/** Steam leaves the description empty on achievements hidden until earned. */
export const buildAchievementRows = (
  progress: GameProgressDto,
  filter: AchievementFilter,
  t: Translate,
): readonly AchievementRow[] =>
  progress.achievements
    .filter((a) => (filter === "unlocked" ? a.unlocked : filter === "locked" ? !a.unlocked : true))
    // Most recently earned first; anything locked settles at the bottom.
    .slice()
    .sort((a, b) => earnedAt(b) - earnedAt(a))
    .map((a) => ({
      apiName: a.apiName,
      name: a.displayName,
      description: a.description === "" ? t("game.hiddenDescription") : a.description,
      // The grey icon is what Steam ships for an achievement not yet earned.
      iconUrl: a.unlocked ? a.icon : a.iconGray,
      unlocked: a.unlocked,
      dateLabel: a.unlockedAt ? formatDay(a.unlockedAt, t) : t("game.locked"),
    }));

export const buildTimelineDays = (
  progress: GameProgressDto,
  t: Translate,
): readonly TimelineDay[] => {
  const byName = new Map(progress.achievements.map((a) => [a.apiName, a]));
  const days = new Map<string, TimelineItem[]>();

  // Newest first, so both the days and each day's rows come out that way.
  const entries = [...progress.timeline].sort(
    (a, b) => Date.parse(b.unlockedAt) - Date.parse(a.unlockedAt),
  );

  for (const entry of entries) {
    const achievement = byName.get(entry.apiName);
    if (!achievement) {
      // The timeline names an achievement the schema does not define; the
      // mapper already drops those, so this is belt and braces.
      continue;
    }
    const key = formatDay(entry.unlockedAt, t);
    const items = days.get(key) ?? [];
    items.push({
      apiName: entry.apiName,
      name: achievement.displayName,
      iconUrl: achievement.icon,
      timeLabel: formatTime(entry.unlockedAt),
    });
    days.set(key, items);
  }

  return [...days.entries()].map(([key, items]) => {
    // "24 Jun 2026" and "24 juin 2026" split into the day line and the year beneath it.
    const parts = key.split(" ");
    const year = parts.pop() ?? "";
    return {
      key,
      day: parts.join(" "),
      year,
      countLabel: t("game.unlockedOnDay", { count: items.length }),
      items,
    };
  });
};

/**
 * Progress is nullable here on purpose: null means the achievements were never
 * fetched, which is not the same as a game that defines none. The mock words
 * those two states differently.
 */
export const buildGameSummary = (
  game: GameDto,
  progress: GameProgressDto | null,
  t: Translate,
): GameSummary => {
  // Steam does not always send a last-played time, and "last played never"
  // beside 82 hours is untrue. Where it withholds the date the line says only
  // what is known; only a game with no playtime either was really never opened.
  // Both can be missing, and neither absence is a figure: Steam withholds
  // playtime across a whole library, so a Game here arrives with its hours
  // already known to be absent rather than zero (CONTEXT.md, Playtime). That is
  // what lets this screen — which holds one Game and no library — say nothing
  // rather than write `0 min played · last played never` over unlocks dated
  // 2010 to 2014.
  //
  // The hours are written here rather than read off the wire: this screen keeps
  // the figure to the minute, where a library row rounds it to fit (ADR-0015).
  const played =
    game.playtimeMinutes === null
      ? null
      : t("game.played", { time: formatPlaytimeExact(game.playtimeMinutes) });
  const lastPlayed = game.lastPlayedAt
    ? t("game.lastPlayed", { day: formatDay(game.lastPlayedAt, t) })
    : neverLaunched(game)
      ? t("game.lastPlayedNever")
      : null;
  const meta = joined([played, lastPlayed]);

  if (progress === null) {
    return {
      percentage: null,
      rateLabel: "—",
      fraction: t("game.notLoaded"),
      remaining: "",
      lastUnlock: "",
      meta,
    };
  }

  const { unlocked, total, percentage } = progress.completion;
  const known = total > 0;
  const left = Math.max(0, total - unlocked);
  const lastUnlockAt = progress.timeline.reduce<string | null>(
    (latest, entry) =>
      latest === null || Date.parse(entry.unlockedAt) > Date.parse(latest)
        ? entry.unlockedAt
        : latest,
    null,
  );

  return {
    percentage: known ? Math.round(percentage) : null,
    rateLabel: known ? `${Math.round(percentage)}%` : "—",
    fraction: known ? `${unlocked} / ${total}` : t("library.noAchievements"),
    remaining: known ? t("game.remaining", { count: left }) : "",
    lastUnlock: lastUnlockAt ? t("game.lastUnlock", { day: formatDay(lastUnlockAt, t) }) : "",
    meta,
  };
};

/**
 * The Game a screen is being asked to show, or null when the player does not
 * own it.
 *
 * ADR-0004: the API answers for whatever appId it is given without checking
 * that the player owns it, so a game outside the library comes back looking
 * exactly like a game with nothing to earn. The refusal lives here instead,
 * because the screen already loads the library — it needs the Game's name,
 * cover and playtime before it can draw anything — and so already holds the
 * answer the API would spend a Steam call to learn.
 *
 * That makes this rule load-bearing rather than defensive: a stale link or a
 * mistyped appId is refused here or nowhere.
 */
export const gameInLibrary = (games: readonly GameDto[], appId: number): GameDto | null =>
  games.find((candidate) => candidate.appId === appId) ?? null;
